import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import { issuePath } from "helpers/issueLinks";
import { Segmented, Select, Skeleton, Empty, Tooltip, Tag } from "antd";
import {
  BarChartOutlined,
  HeatMapOutlined,
  TeamOutlined,
  UserOutlined,
  WarningOutlined,
  BulbOutlined,
  RightOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  RiseOutlined,
  FileZipOutlined,
  ProjectOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  getCompanyOverview,
  getCompanyContributions,
  getCompanyRecommendations,
} from "api/analytics/projectAnalyticsApi";

export const T = {
  ink: "#23343E", text: "#3A4952", muted: "#8D8D8D", sub: "#AEAEAE",
  line: "#EFEFEF", line2: "#E2E2E2", bg: "#FAFAFA", wash: "#E9F1F5",
  wash2: "#F3F7F9", navy: "#455D6B", navyD: "#3A5765", navyX: "#23343E",
  orange: "#FE6919", added: "#4FC92A", addedBg: "#EEFBE9", added2: "#317A1B",
  removed: "#FB8080", removedBg: "#FFF0F0", removed2: "#C53030",
  amber: "#E0A43A", ambBg: "#FDF4E1",
  purple: "#5765D4",
};

export function Card({ title, subtitle, children, style, extra }) {
  return (
    <div style={{
      background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8,
      padding: 16, ...style,
    }}>
      {(title || extra) && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: subtitle ? 2 : 12 }}>
          {title && <div style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>{title}</div>}
          <span style={{ flex: 1 }} />
          {extra}
        </div>
      )}
      {subtitle && <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 12 }}>{subtitle}</div>}
      {children}
    </div>
  );
}

export function Stat({ label, value, color = T.ink, icon }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, color: T.muted, fontWeight: 600, letterSpacing: ".06em", marginBottom: 4 }}>
        {label.toUpperCase()}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color, display: "flex", alignItems: "center", gap: 8 }}>
        {icon}{value}
      </div>
    </div>
  );
}

export function Pill({ kind = "neutral", children, size = "sm" }) {
  const pad = size === "sm" ? "2px 7px" : "3px 9px";
  const fs = size === "sm" ? 9.5 : 10.5;
  const map = {
    neutral: { bg: "#F3F4F5", bd: "#E8EAEC", fg: "#526470" },
    navy: { bg: T.wash, bd: "#D4E3EC", fg: T.navyD },
    added: { bg: T.addedBg, bd: "#CDEEC1", fg: T.added2 },
    removed: { bg: T.removedBg, bd: "#FAD6D6", fg: T.removed2 },
    orange: { bg: "#FFF2E9", bd: "#FFD9BF", fg: "#B84B0A" },
    amber: { bg: T.ambBg, bd: "#F1DFB1", fg: "#8A6112" },
  };
  const s = map[kind] || map.neutral;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: s.bg, border: `1px solid ${s.bd}`, color: s.fg,
      padding: pad, borderRadius: 999,
      fontSize: fs, fontWeight: 600, lineHeight: 1, letterSpacing: ".02em",
    }}>{children}</span>
  );
}

function BarsRow({ days, daily, maxDay, height = 50 }) {
  if (!days?.length) return null;
  const peak = Math.max(1, maxDay);
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(${days.length}, 1fr)`,
      gap: 1, height, alignItems: "end",
    }}>
      {days.map(d => {
        const count = daily[d] || 0;
        const h = count ? Math.max(3, (count / peak) * height) : 2;
        return (
          <Tooltip key={d} title={`${moment(d).format("MMM D")} — ${count} event${count === 1 ? "" : "s"}`}>
            <div style={{
              height: h,
              background: count ? T.purple : T.line2,
              opacity: count ? Math.max(0.3, count / peak) : 1,
              borderRadius: 1,
            }} />
          </Tooltip>
        );
      })}
    </div>
  );
}

function HeatmapGrid({ days, daily, maxDay }) {
  if (!days?.length) return null;
  const peak = Math.max(1, maxDay);
  const weeks = [];
  let currentWeek = new Array(7).fill(null);
  days.forEach(d => {
    const dow = moment(d).day();
    if (dow === 0 && currentWeek.some(Boolean)) {
      weeks.push(currentWeek);
      currentWeek = new Array(7).fill(null);
    }
    currentWeek[dow] = d;
  });
  if (currentWeek.some(Boolean)) weeks.push(currentWeek);

  const colorFor = (count) => {
    if (!count) return "#ECEEF3";
    const t = count / peak;
    if (t < 0.25) return "#D4D8F2";
    if (t < 0.5) return "#A7AFEA";
    if (t < 0.75) return "#8E98E4";
    return T.purple;
  };

  return (
    <div style={{ display: "flex", gap: 2 }}>
      {weeks.map((week, wi) => (
        <div key={wi} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {week.map((d, di) => {
            if (!d) return <div key={di} style={{ width: 11, height: 11 }} />;
            const count = daily[d] || 0;
            return (
              <Tooltip key={di} title={`${moment(d).format("ddd MMM D")} — ${count} event${count === 1 ? "" : "s"}`}>
                <div style={{
                  width: 11, height: 11, borderRadius: 2,
                  background: colorFor(count),
                }} />
              </Tooltip>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export function StatusBreakdown({ counts }) {
  const items = [
    { key: "open", label: "Open", color: "#3B82F6", icon: <ClockCircleOutlined /> },
    { key: "pending", label: "Pending", color: "#F59E0B", icon: <ExclamationCircleOutlined /> },
    { key: "resolved", label: "Resolved", color: "#10B981", icon: <CheckCircleOutlined /> },
    { key: "abandoned", label: "Abandoned", color: "#6B7280", icon: <StopOutlined /> },
  ];
  const total = Object.values(counts || {}).reduce((a, b) => a + b, 0) || 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{
        display: "flex", height: 10, borderRadius: 999, overflow: "hidden",
        background: "#F3F4F5",
      }}>
        {items.map(it => {
          const pct = ((counts?.[it.key] || 0) / total) * 100;
          return <div key={it.key} style={{ width: `${pct}%`, background: it.color }} />;
        })}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
        {items.map(it => (
          <div key={it.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
            <span style={{
              width: 8, height: 8, borderRadius: "50%", background: it.color, flexShrink: 0,
            }} />
            <span style={{ color: T.text }}>{it.label}</span>
            <span style={{ flex: 1 }} />
            <span style={{ fontWeight: 600, color: T.ink }}>{counts?.[it.key] || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Status dot colours shared by the issue rows below.
const STATUS_DOT = { open: "#3B82F6", pending: "#F59E0B", under_review: "#8B5CF6", resolved: "#10B981", abandoned: "#6B7280" };

// One issue in an analytics list: label, title, status dot and a link to
// the issue itself. `compact` drops the project name.
export function IssueRow({ issue, compact }) {
  const to = issuePath(issue);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 0", minWidth: 0, borderTop: `1px dashed ${T.line}` }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: STATUS_DOT[issue.status] || STATUS_DOT.open }}
            title={issue.status || "open"} />
      {issue.label && (
        <span style={{ fontSize: 10, fontWeight: 600, color: T.muted, fontFamily: "monospace", flexShrink: 0 }}>{issue.label}</span>
      )}
      <Link to={to} title={issue.title}
            style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {issue.title}
        {!compact && issue.projectTitle && <span style={{ color: T.sub }}> · {issue.projectTitle}</span>}
      </Link>
      <Link to={to} className="issue-view-btn"
            style={{ fontSize: 10.5, fontWeight: 600, color: T.navyD, border: `1px solid #D4E3EC`, background: T.wash, borderRadius: 4, padding: "1px 7px", flexShrink: 0, whiteSpace: "nowrap" }}>
        View issue
      </Link>
    </div>
  );
}

// Priority columns. With `issues` ({urgent:[], normal:[], low:[]}, the
// rows behind the counts) each column lists its issues - the first few,
// then "Show all" - with a link to every one of them.
const PRIORITY_PREVIEW = 5;
export function PriorityBreakdown({ counts, issues }) {
  const [expanded, setExpanded] = useState({});
  const items = [
    { key: "urgent", label: "Urgent", color: T.removed2, bg: T.removedBg },
    { key: "normal", label: "Normal", color: T.navyD, bg: T.wash },
    { key: "low", label: "Low", color: T.muted, bg: "#F3F4F5" },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, alignItems: "start" }}>
      {items.map(it => {
        const list = issues?.[it.key] || [];
        const open = !!expanded[it.key];
        const shown = open ? list : list.slice(0, PRIORITY_PREVIEW);
        return (
          <div key={it.key} style={{
            padding: "8px 10px", background: it.bg, borderRadius: 6, minWidth: 0,
          }}>
            <div style={{ fontSize: 10, color: it.color, fontWeight: 600, letterSpacing: ".06em", marginBottom: 3 }}>
              {it.label.toUpperCase()}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: it.color }}>{counts?.[it.key] || 0}</div>
            {list.length > 0 && (
              <div style={{ marginTop: 6 }}>
                {shown.map(issue => <IssueRow key={issue.id} issue={issue} compact />)}
                {list.length > PRIORITY_PREVIEW && (
                  <button type="button"
                    onClick={() => setExpanded(e => ({ ...e, [it.key]: !open }))}
                    style={{ marginTop: 4, background: "transparent", border: 0, padding: 0, fontSize: 11, fontWeight: 600, color: it.color, cursor: "pointer" }}>
                    {open ? "Show less" : `Show all ${list.length}`}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function RecoBucket({ title, icon, color, bg, items }) {
  if (!items?.length) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{
          width: 22, height: 22, borderRadius: "50%", background: bg,
          color, display: "inline-flex", alignItems: "center", justifyContent: "center",
          fontSize: 11,
        }}>{icon}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: T.ink, letterSpacing: ".02em" }}>{title}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((r, i) => (
          <div key={i} style={{
            padding: "10px 12px", border: `1px solid ${T.line2}`, borderRadius: 6,
            borderLeft: `3px solid ${color}`,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 3 }}>{r.title}</div>
            <div style={{ fontSize: 11.5, color: T.text, lineHeight: 1.5 }}>{r.body}</div>
            {r.items?.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {r.items.map(it => <IssueRow key={it.id} issue={it} />)}
                {r.count > r.items.length && (
                  <div style={{ fontSize: 10.5, color: T.muted, marginTop: 4 }}>+{r.count - r.items.length} more in Issues</div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CompanyAnalyticsPanel({
  days: daysProp,
  onDaysChange,
  projectIds,
  disciplineIds,
  milestoneIds,
  userIds,
  revisionIds,
} = {}) {
  const history = useHistory();
  const [overview, setOverview] = useState(null);
  const [contrib, setContrib] = useState(null);
  const [recos, setRecos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Support both controlled (from a parent filter bar) and uncontrolled usage.
  const [daysInner, setDaysInner] = useState(90);
  const days = daysProp != null ? daysProp : daysInner;
  const setDays = onDaysChange || setDaysInner;
  const [viewMode, setViewMode] = useState("individuals"); // default: per contributor, not the team aggregate
  const [chartStyle, setChartStyle] = useState("heatmap");
  const [selectedUserIds, setSelectedUserIds] = useState(null);

  // Stable string keys for deps so arrays don't trigger infinite re-fetches
  const projKey = (projectIds || []).join(",");
  const discKey = (disciplineIds || []).join(",");
  const mileKey = (milestoneIds || []).join(",");
  const userKey = (userIds || []).join(",");
  const revKey = (revisionIds || []).join(",");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    const filters = { projectIds, disciplineIds, milestoneIds, userIds, revisionIds };
    Promise.all([
      getCompanyOverview(filters),
      getCompanyContributions(days, filters),
      getCompanyRecommendations(filters),
    ])
      .then(([ov, co, rc]) => {
        if (cancelled) return;
        setOverview(ov); setContrib(co); setRecos(rc);
      })
      .catch(e => !cancelled && setErr(e.message || "Failed to load"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, projKey, discKey, mileKey, userKey, revKey]);

  const contributors = contrib?.contributors || [];
  const dayList = contrib?.days || [];

  const filteredContributors = useMemo(() => {
    if (!selectedUserIds) return contributors;
    return contributors.filter(c => selectedUserIds.includes(String(c.user._id)));
  }, [contributors, selectedUserIds]);

  const teamAggregate = useMemo(() => {
    if (!dayList.length) return null;
    const daily = {};
    dayList.forEach(d => { daily[d] = 0; });
    let total = 0, maxDay = 0, activeDays = 0;
    filteredContributors.forEach(c => {
      dayList.forEach(d => { const v = c.daily[d] || 0; daily[d] += v; total += v; });
    });
    dayList.forEach(d => {
      if (daily[d] > maxDay) maxDay = daily[d];
      if (daily[d] > 0) activeDays += 1;
    });
    return { daily, total, maxDay, activeDays };
  }, [dayList, filteredContributors]);

  if (loading) {
    return (
      <div style={{ padding: 16 }}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }
  if (err) {
    return (
      <div style={{
        padding: 14, background: T.removedBg, border: "1px solid #FAD6D6",
        borderRadius: 6, color: T.removed2, margin: 16,
      }}>{err}</div>
    );
  }

  const reco = recos?.recommendations || { urgent: [], recommended: [], next: [] };
  const hasAnyReco = reco.urgent.length + reco.recommended.length + reco.next.length > 0;

  return (
    <div style={{ padding: "0 0 16px" }}>
      {/* Title row */}
      <div style={{
        display: "flex", alignItems: "baseline", justifyContent: "space-between",
        margin: "0 4px 14px",
      }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: T.ink, letterSpacing: "-.01em" }}>
            Project analytics
          </div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
            {overview?.projectCount || 0} project{overview?.projectCount === 1 ? "" : "s"} · {overview?.memberCount || 0} member{overview?.memberCount === 1 ? "" : "s"} · {overview?.activityLast30Days || 0} events in last 30 days
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16,
      }}>
        <Card style={{ padding: 16 }}>
          <Stat label="Total issues" value={overview?.issues?.total || 0}
                icon={<FileZipOutlined style={{ color: T.navy, fontSize: 18 }} />} />
        </Card>
        <Card style={{ padding: 16 }}>
          <Stat label="Open + Pending" color="#B45309"
                value={(overview?.issues?.byStatus?.open || 0) + (overview?.issues?.byStatus?.pending || 0)} />
        </Card>
        <Card style={{ padding: 16 }}>
          <Stat label="Exports (30d)" color={T.added2} value={overview?.exports?.last30Days || 0} />
        </Card>
        <Card style={{ padding: 16 }}>
          <Stat label="Active projects" value={overview?.projectCount || 0}
                icon={<ProjectOutlined style={{ color: T.navyD, fontSize: 18 }} />} />
        </Card>
      </div>

      {/* Issues + Recommendations */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16,
      }}>
        <Card title="Issues by status">
          <div style={{ marginBottom: 12 }}>
            <StatusBreakdown counts={overview?.issues?.byStatus} />
          </div>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginBottom: 6 }}>
            BY PRIORITY
          </div>
          <PriorityBreakdown counts={overview?.issues?.byPriority} issues={overview?.issues?.byPriorityIssues} />
        </Card>

        <Card
          title="Recommendations"
          extra={<Pill kind="neutral" size="sm">Rule-based</Pill>}
        >
          <RecoBucket title="Urgent" icon={<WarningOutlined />}
            color={T.removed2} bg={T.removedBg} items={reco.urgent} />
          <RecoBucket title="Recommended" icon={<BulbOutlined />}
            color={"#8A6112"} bg={T.ambBg} items={reco.recommended} />
          <RecoBucket title="Next action" icon={<RightOutlined />}
            color={T.navyD} bg={T.wash} items={reco.next} />
          {!hasAnyReco && <Empty description="No action items" />}
          <div style={{
            marginTop: 8, fontSize: 10.5, color: T.muted, fontStyle: "italic",
          }}>
            {recos?.disclaimer}
          </div>
        </Card>
      </div>

      {/* Contributor activity */}
      <Card
        title={<>
          <span>Contributor activity</span>
          <span style={{ fontSize: 11, color: T.muted, marginLeft: 8, fontWeight: 400 }}>
            events per day · {contributors.length} contributor{contributors.length === 1 ? "" : "s"}
          </span>
        </>}
        extra={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Select size="small" style={{ width: 110 }}
              value={days} onChange={setDays}
              options={[
                { value: 30, label: "30 days" },
                { value: 60, label: "60 days" },
                { value: 90, label: "90 days" },
                { value: 180, label: "180 days" },
              ]}
            />
            <Segmented size="small" value={viewMode} onChange={setViewMode}
              options={[
                { value: "team", label: "Team", icon: <TeamOutlined /> },
                { value: "individuals", label: "Individuals", icon: <UserOutlined /> },
              ]}
            />
            <Segmented size="small" value={chartStyle} onChange={setChartStyle}
              options={[
                { value: "bars", icon: <BarChartOutlined /> },
                { value: "heatmap", icon: <HeatMapOutlined /> },
              ]}
            />
          </div>
        }
        style={{ marginBottom: 16 }}
      >
        {contributors.length > 1 && viewMode === "individuals" && (
          <div style={{ marginBottom: 12 }}>
            <Select mode="multiple" size="small" style={{ width: "100%" }}
              placeholder="Filter contributors"
              value={selectedUserIds || contributors.map(c => String(c.user._id))}
              onChange={(v) => setSelectedUserIds(v.length === contributors.length ? null : v)}
              options={contributors.map(c => ({
                value: String(c.user._id),
                label: `${c.user.name} (${c.total})`,
              }))}
            />
          </div>
        )}

        {contributors.length === 0 ? (
          <Empty description="No contributor activity in range" />
        ) : viewMode === "team" ? (
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.ink, marginBottom: 6 }}>
              Team aggregate · {teamAggregate?.total || 0} events
              <span style={{ fontSize: 11, fontWeight: 400, color: T.muted, marginLeft: 8 }}>
                active {teamAggregate?.activeDays || 0}/{dayList.length} days · peak {teamAggregate?.maxDay || 0}/day
              </span>
            </div>
            {chartStyle === "bars" ? (
              <BarsRow days={dayList} daily={teamAggregate?.daily || {}} maxDay={teamAggregate?.maxDay || 0} height={72} />
            ) : (
              <HeatmapGrid days={dayList} daily={teamAggregate?.daily || {}} maxDay={teamAggregate?.maxDay || 0} />
            )}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {filteredContributors.map(c => (
              <div key={c.user._id}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: "50%", background: T.purple,
                    color: "#fff", fontSize: 10, fontWeight: 600,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}>{c.user.name.charAt(0).toUpperCase()}</div>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>{c.user.name}</span>
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 11, color: T.muted }}>
                    {c.total} events · {c.activeDays} active days
                  </span>
                </div>
                {chartStyle === "bars" ? (
                  <BarsRow days={dayList} daily={c.daily} maxDay={c.maxDay} height={42} />
                ) : (
                  <HeatmapGrid days={dayList} daily={c.daily} maxDay={c.maxDay} />
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Recent lists */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12,
      }}>
        <Card title="Latest exports">
          {(overview?.latestExports || []).length === 0 ? (
            <Empty description="No exports yet" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {overview.latestExports.map(e => (
                <div key={e.id} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "8px 10px", background: T.bg, borderRadius: 6,
                }}>
                  <FileZipOutlined style={{ color: T.navy }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 500, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {e.title || "Export"}
                    </div>
                    <div style={{ fontSize: 11, color: T.muted }}>
                      {moment(e.date).fromNow()}
                      {e.author ? ` · ${e.author.name}` : ""}
                      {e.milestone ? ` · ${e.milestone}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Recent issues">
          {(overview?.recentIssues || []).length === 0 ? (
            <Empty description="No issues yet" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {overview.recentIssues.map(m => (
                <div key={m.id}
                  onClick={() => history.push(issuePath(m))}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 10px", background: T.bg, borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 500, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m.label && <span style={{ fontFamily: "monospace", fontSize: 11, color: T.muted, marginRight: 6 }}>{m.label}</span>}
                      {m.title}
                    </div>
                    <div style={{ fontSize: 11, color: T.muted }}>
                      {moment(m.updatedAt).fromNow()}
                      {m.projectTitle ? ` · ${m.projectTitle}` : ""}
                      {m.author?.name ? ` · ${m.author.name}` : ""}
                    </div>
                  </div>
                  <Tag color={
                    m.status === "resolved" ? "green" :
                    m.status === "pending" ? "orange" :
                    m.status === "abandoned" ? "default" : "blue"
                  } style={{ margin: 0, fontSize: 10 }}>
                    {(m.status || "open").toUpperCase()}
                  </Tag>
                  {m.priority === "urgent" && <Pill kind="removed" size="sm"><RiseOutlined /> URGENT</Pill>}
                  <Link to={issuePath(m)} onClick={(e) => e.stopPropagation()}
                        style={{ fontSize: 10.5, fontWeight: 600, color: T.navyD, border: "1px solid #D4E3EC", background: T.wash, borderRadius: 4, padding: "2px 8px", whiteSpace: "nowrap" }}>
                    View issue
                  </Link>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
